import { useState, useEffect } from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import Backdrop from '@mui/material/Backdrop';
import BaseLineDialog from '../dialog/BaselineDialog';
import { useDispatch, useSelector } from 'react-redux';
import ganttStore from '../../ganttConfig/ganttStore';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CircularProgress from '@mui/material/CircularProgress';
import SaveIconComponent from '@blackhyve/common/src/icons/Save';
import EditIconComponent from '@blackhyve/common/src/icons/Edit';
import ResetIconComponent from '@blackhyve/common/src/icons/Reset';
import CheckIconComponent from '@blackhyve/common/src/icons/Check';
import DeleteIconComponent from '@blackhyve/common/src/icons/Delete';
import ArrowLeftIconComponent from '@blackhyve/common/src/icons/ArrowLeft';
import { ConfirmDeleteDialog, MoreOptionDialog } from '@blackhyve/common';
import {
  clearSelectedBaseline,
  deleteBaseline,
  fetchBaselines,
  fetchBaselineTasks,
} from 'features/tasks/store/tasksSlice';
import { MenuButton } from 'assets/style-components/button';
import { addBaselineLayer } from '../../ganttConfig/baseline';

/**
 * Baselines
 * @param {string} projectId
 * @returns
 */
const BaselineMenu = ({ projectId, ganttId }) => {
  const dispatch = useDispatch();
  const gantt = ganttStore.getGantt(ganttId);
  const [loading, setLoading] = useState(false);
  const [menuState, setMenuState] = useState(null);
  const [popupState, setPopupState] = useState({});
  const [baselineOptionList, setBaselineOptionList] = useState([]);
  const baselines = useSelector((state) => state.tasks.baselines.list);
  const selectedBaseline = useSelector((state) => state.tasks.baselines.selected);

  const handleCloseMenu = () => {
    setMenuState(false);
  };

  const handleClosePopup = () => {
    setPopupState({});
  };

  const handleResetDefault = () => {
    setLoading(true);
    gantt.batchUpdate(() => {
      gantt.eachTask((task) => {
        if (!task.placeholder) {
          task.baseline_start = null;
          task.baseline_end = null;
        }
      });
    });
    setLoading(false);
    handleCloseMenu();
    dispatch(clearSelectedBaseline());
  };

  const handleSelectBaseline = (requestParameter) => {
    setLoading(true);
    handleCloseMenu();
    dispatch(fetchBaselineTasks(requestParameter.id))
      .then((response) => {
        addBaselineLayer(ganttId);
        gantt.batchUpdate(() => {
          for (let i = 0; i < response.payload?.tasks?.length; i++) {
            const baselineObj = response.payload.tasks[i];
            const task = gantt.getTask(baselineObj.task_id);
            task.baseline_start = gantt.date.parseDate(baselineObj.start, '%Y-%m-%d');
            task.baseline_end = moment(baselineObj.end).add(1, 'day').toDate();
          }
        });
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleDeleteConfirmation = (requestParameter) => {
    setPopupState({ popupType: 'delete-confirmation', id: requestParameter.id });
  };

  const handleDeleteBaseline = (id) => {
    dispatch(deleteBaseline(id));
  };

  const handleOpenEditDialog = (requestParameter) => {
    setPopupState({ popupType: 'edit-dialog', ...requestParameter });
  };

  const anchorPosition = {
    vertical: 'bottom',
    horizontal: 'left',
  };

  useEffect(() => {
    let baselineList = [];
    if (!isEmpty(baselines)) {
      baselineList = Object.values(baselines)?.map(({ id, name }) => {
        return {
          name,
          id,
          icon: <ArrowLeftIconComponent height={14} viewBox={'0 0 16 24'} width={16} />,
          isCustomView: true,
          subItems: [
            {
              id,
              name: 'Select',
              icon: <CheckIconComponent height={16} viewBox={'0 0 12 10'} width={16} />,
              action: handleSelectBaseline,
            },
            {
              id,
              name: 'Rename',
              icon: <EditIconComponent height={14} width={16} />,
              action: handleOpenEditDialog,
            },
            {
              id,
              name: 'Delete',
              icon: <DeleteIconComponent color="red" height={16} width={16} />,
              style: { color: 'red' },
              action: handleDeleteConfirmation,
            },
          ],
        };
      });
    }
    baselineList.push(
      {
        name: 'Reset',
        isCustomView: true,
        icon: <ResetIconComponent height={16} width={16} />,
        action: handleResetDefault,
      },
      {
        name: 'Create Baseline',
        isCustomView: true,
        icon: <SaveIconComponent height={16} width={16} />,
        action: handleOpenEditDialog,
      }
    );
    setBaselineOptionList(baselineList);
  }, [baselines]);

  useEffect(() => {
    dispatch(fetchBaselines(projectId));
  }, []);

  return (
    <>
      <MenuButton
        endIcon={Boolean(menuState) ? <ArrowDropUp /> : <ArrowDropDown />}
        isHighlighted={selectedBaseline}
        onClick={(event) => setMenuState(event.currentTarget)}
      >
        Baseline {selectedBaseline ? selectedBaseline.name : ''}
      </MenuButton>
      {loading && (
        <Backdrop open={true} style={{ zIndex: 13 }} sx={{ color: '#fff' }}>
          <CircularProgress className="loading" />
        </Backdrop>
      )}
      {menuState && (
        <MoreOptionDialog
          anchorEl={menuState}
          anchorPosition={anchorPosition}
          handleClose={handleCloseMenu}
          open={true}
          optionMenu={baselineOptionList}
        />
      )}
      {popupState?.popupType === 'delete-confirmation' && (
        <ConfirmDeleteDialog
          handleClose={handleClosePopup}
          handleDelete={handleDeleteBaseline}
          id={popupState.id}
          item={'baseline'}
          open={true}
        />
      )}
      {popupState?.popupType === 'edit-dialog' && (
        <BaseLineDialog
          handleClose={handleClosePopup}
          id={popupState.id}
          open={true}
          projectId={projectId}
        />
      )}
    </>
  );
};

export default BaselineMenu;
